/**
 * FieldScoutRecording — the mobile recording screen.
 *
 * Composes useFieldScoutRecording (state/handlers/GPS), DotGrid, HScroll,
 * IncidenceButton/LevelButton/CountButton and FieldScoutReview into a single
 * full-viewport dark chrome recording experience.
 *
 * Track: field_scout_recording_mobile / Slice 2 / Task S2-11.
 *
 * Spec refs:
 *   §3.1 layout (fixed dark header + progress strip + scrollable body + fixed action bar)
 *   §3.6 GPS traffic-light colouring
 *   §7   GPS dot thresholds (<5 green / 5–10 amber / >10 red)
 *
 * CSS rules (non-negotiable):
 *   NO CSS transition on background-color/border-color of any state-toggling element.
 *   transform: scale(.95) press transitions (GPU composited) are fine.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Pest } from '../../../db';
import { pct, examinedCount } from '../../../scouting/recording';
import { normalizeMeasurementType, typeDefaultThreshold } from '../../../scouting/pestConfig';
import type { useFieldScoutRecording } from './useFieldScoutRecording';
import { DotGrid } from './DotGrid';
import { HScroll } from './HScroll';
import { IncidenceButton, LevelButton, CountButton } from './PestButtons';
import './FieldScoutRecording.css';

// ─────────────────────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────────────────────

function actionThreshold(p: Pest): number {
  return typeof p.actionThreshold === 'number'
    ? p.actionThreshold
    : typeDefaultThreshold(normalizeMeasurementType(p.measurementType));
}

/** GPS traffic-light: <5 → green / 5–10 → amber / >10 → red. */
function gpsColorClass(accuracy: number): string {
  if (accuracy < 5) return 'fsr-gps--green';
  if (accuracy <= 10) return 'fsr-gps--amber';
  return 'fsr-gps--red';
}

// ─────────────────────────────────────────────────────────────────────────────
//  Props
// ─────────────────────────────────────────────────────────────────────────────

export interface FieldScoutRecordingProps {
  rec: ReturnType<typeof useFieldScoutRecording>;
  pests: Pest[];
  orchardName: string;
  cultivar?: string;
  /** Called when the user presses Finish & review (or the back-to-selection flow). */
  onFinish: () => void;
  /** Called to abort the session (discard the draft) and return to selection. */
  onCancel: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Carousel paging — columns visible per page (whole buttons only)
// ─────────────────────────────────────────────────────────────────────────────

/** Columns of buttons visible per carousel page (matches the prototype's 3-up). */
const COLS = 3;

/** Split a list into fixed-size pages (column-major fill happens via CSS grid). */
function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Collapsible section
// ─────────────────────────────────────────────────────────────────────────────

interface SectionProps {
  title: string;
  summary?: React.ReactNode;
  pinned?: boolean;
  open: boolean;
  onToggle?: () => void;
  typeKey: 'inc' | 'lev' | 'cnt';
  children: React.ReactNode;
  hint?: string;
}

const Section: React.FC<SectionProps> = ({
  title, summary, pinned, open, onToggle, typeKey, children, hint,
}) => (
  <div className={['fsr-section', pinned ? 'fsr-section--pinned' : ''].filter(Boolean).join(' ')}>
    <div
      className={['fsr-section__head', open ? 'fsr-section__head--open' : ''].filter(Boolean).join(' ')}
      onClick={pinned ? undefined : onToggle}
      role={pinned ? undefined : 'button'}
      tabIndex={pinned ? undefined : 0}
      onKeyDown={pinned ? undefined : (e) => { if (e.key === 'Enter' || e.key === ' ') onToggle?.(); }}
    >
      {!pinned && <span className="fsr-section__chevron" aria-hidden>▶</span>}
      <span className={`fsr-section__key fsr-section__key--${typeKey}`} aria-hidden />
      <span className={`fsr-section__title fsr-section__title--${typeKey}`}>{title}</span>
      {summary && <span className="fsr-section__summary">{summary}</span>}
    </div>
    {open && (
      <div className="fsr-section__body">
        {children}
        {hint && <div className="fsr-section__hint">{hint}</div>}
      </div>
    )}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
//  Main component
// ─────────────────────────────────────────────────────────────────────────────

export const FieldScoutRecording: React.FC<FieldScoutRecordingProps> = ({
  rec,
  pests,
  orchardName,
  cultivar,
  onFinish,
  onCancel,
}) => {
  const { t } = useTranslation();

  // ── Partition pests by measurement type ──────────────────────────────────
  const incidencePests = pests.filter(
    (p) => normalizeMeasurementType(p.measurementType) === 'incidence',
  );
  const levelPests = pests.filter(
    (p) => normalizeMeasurementType(p.measurementType) === 'level',
  );
  const countPests = pests.filter(
    (p) => normalizeMeasurementType(p.measurementType) === 'count',
  );

  // ── Collapsible sections state ────────────────────────────────────────────
  const [levelOpen, setLevelOpen] = useState(true);
  const [countOpen, setCountOpen] = useState(true);

  // ── Undo toast state ──────────────────────────────────────────────────────
  const [undoVisible, setUndoVisible] = useState(false);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Watch undo stack via undoStack ref — we show the toast whenever the hook
  // pushes a new item (indicated by changes in mutable state). The simplest
  // approach: intercept actions that push undo by wrapping them here.
  const showUndoToast = useCallback(() => {
    setUndoVisible(true);
    if (undoTimerRef.current !== null) clearTimeout(undoTimerRef.current);
    undoTimerRef.current = setTimeout(() => {
      setUndoVisible(false);
    }, 4000);
  }, []);

  // Cleanup on unmount
  useEffect(() => () => { if (undoTimerRef.current) clearTimeout(undoTimerRef.current); }, []);

  // Wrap action callbacks to trigger the undo toast
  const handleToggleIncidence = useCallback((pestId: string) => {
    rec.toggleIncidence(pestId);
    showUndoToast();
  }, [rec, showUndoToast]);

  const handleRaiseLevel = useCallback((pestId: string) => {
    rec.raiseLevel(pestId);
    showUndoToast();
  }, [rec, showUndoToast]);

  const handleLowerLevel = useCallback((pestId: string) => {
    rec.lowerLevel(pestId);
    showUndoToast();
  }, [rec, showUndoToast]);

  const handleIncCount = useCallback((pestId: string) => {
    rec.incCount(pestId);
    showUndoToast();
  }, [rec, showUndoToast]);

  const handleDecCount = useCallback((pestId: string) => {
    rec.decCount(pestId);
    showUndoToast();
  }, [rec, showUndoToast]);

  const handleClearPoint = useCallback(() => {
    rec.clearPoint();
    showUndoToast();
  }, [rec, showUndoToast]);

  const handleUndo = useCallback(() => {
    rec.undo();
    setUndoVisible(false);
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
  }, [rec]);

  // ── Photo capture ─────────────────────────────────────────────────────────
  const photoInputRef = useRef<HTMLInputElement>(null);
  const handlePhotoClick = () => photoInputRef.current?.click();
  const handlePhotoChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) rec.capturePhoto(file);
      // Reset so the same file can be selected again
      e.target.value = '';
    },
    [rec],
  );

  // ── GPS soft-block gate (issue #392) ──────────────────────────────────────
  // Gate a poor OR missing fix (rec.isAccuracyPoor: accuracy > 15 m, or no fix
  // yet). Only the *advance* is gated — pest marks are already saved; Override
  // always lets the scouter proceed (recording without coords if there's no fix).
  const [gpsGateOpen, setGpsGateOpen] = useState(false);
  const pendingProceed = useRef<null | (() => void)>(null);

  const attemptCommit = useCallback((proceed: () => void) => {
    const needsStamp = rec.pointCoords[rec.currentPoint] === undefined;
    if (needsStamp && rec.isAccuracyPoor && !rec.isManualOverride) {
      pendingProceed.current = proceed;
      setGpsGateOpen(true);
      return;
    }
    rec.stampCurrentPoint();
    proceed();
  }, [rec]);

  const handleNext = useCallback(() => attemptCommit(rec.next), [attemptCommit, rec.next]);
  const handleFinish = useCallback(() => attemptCommit(onFinish), [attemptCommit, onFinish]);

  const handleGateWait = useCallback(() => {
    pendingProceed.current = null;
    setGpsGateOpen(false);
  }, []);

  const handleGateOverride = useCallback(() => {
    rec.triggerManualOverride();
    rec.stampCurrentPoint();
    const proceed = pendingProceed.current;
    pendingProceed.current = null;
    setGpsGateOpen(false);
    proceed?.();
  }, [rec]);

  // ── Derived values ────────────────────────────────────────────────────────
  const examined = examinedCount(rec.maxExamined);
  const activePest = pests.find((p) => p.id === rec.activePestId);
  const activePestInfestedCount = rec.infested[rec.activePestId]?.length ?? 0;
  const activePct = pct(activePestInfestedCount, examined);
  const isOverThreshold = activePest != null && activePct >= actionThreshold(activePest);
  const isLastPoint = rec.currentPoint >= 99 || examined >= 100;

  // All infested union (for "all" mode in DotGrid)
  const allInfestedSet = React.useMemo(() => {
    const s = new Set<number>();
    for (const ids of Object.values(rec.infested)) {
      for (const id of ids) s.add(id);
    }
    return s;
  }, [rec.infested]);

  const activeInfestedSet = React.useMemo(
    () => new Set<number>(rec.infested[rec.activePestId] ?? []),
    [rec.infested, rec.activePestId],
  );

  const photoPointSet = React.useMemo(
    () => new Set<number>(Object.keys(rec.photos).map(Number)),
    [rec.photos],
  );

  // Progress bar width (examined / 100)
  const progressWidth = Math.min(100, (examined / 100) * 100);

  // GPS accuracy display
  const accuracyDisplay = isFinite(rec.currentAccuracy)
    ? t('fieldScout.recording.gpsAccuracy', { m: Math.round(rec.currentAccuracy) })
    : '…';

  const gpsClass = isFinite(rec.currentAccuracy)
    ? gpsColorClass(rec.currentAccuracy)
    : 'fsr-gps--red';

  const geofenceMismatch =
    rec.detectedOrchardName !== 'Unknown' && rec.detectedOrchardName !== orchardName;

  // ── Incidence section summary ─────────────────────────────────────────────
  const incidenceHotCount = incidencePests.filter((p) => {
    const c = rec.infested[p.id]?.length ?? 0;
    return pct(c, examined) >= actionThreshold(p);
  }).length;

  const incidenceSummary = (
    <>
      <span className="fsr-chip fsr-chip--inc">
        {t('fieldScout.recording.pests', { count: incidencePests.length })}
      </span>
      {incidenceHotCount > 0 && (
        <span className="fsr-chip fsr-chip--over">
          {t('fieldScout.recording.overCount', { count: incidenceHotCount })}
        </span>
      )}
    </>
  );

  // ── Level section summary ─────────────────────────────────────────────────
  const levelOverCount = levelPests.filter(
    (p) => (rec.levels[p.id] ?? 0) >= actionThreshold(p),
  ).length;

  const levelSummary = (
    <>
      <span className="fsr-chip fsr-chip--lev">
        {t('fieldScout.recording.pests', { count: levelPests.length })}
      </span>
      {levelOverCount > 0 && (
        <span className="fsr-chip fsr-chip--over">
          {t('fieldScout.recording.overCount', { count: levelOverCount })}
        </span>
      )}
    </>
  );

  // ── Count section summary ─────────────────────────────────────────────────
  const countOverCount = countPests.filter(
    (p) => (rec.counts[p.id] ?? 0) >= actionThreshold(p),
  ).length;

  const countSummary = (
    <>
      <span className="fsr-chip fsr-chip--cnt">
        {t('fieldScout.recording.pests', { count: countPests.length })}
      </span>
      {countOverCount > 0 && (
        <span className="fsr-chip fsr-chip--over">
          {t('fieldScout.recording.overCount', { count: countOverCount })}
        </span>
      )}
    </>
  );

  const handleCancel = () => {
    // Aborting discards the whole in-progress session — confirm first (this is a
    // deliberate, infrequent action, not a tap-speed one, so a confirm is warranted).
    if (window.confirm(t('fieldScout.recording.cancelConfirm'))) {
      onCancel();
    }
  };

  return (
    <div className="fsr-recording" data-testid="field-scout-recording">
      {/* ── Fixed dark header ─────────────────────────────────────────────── */}
      <header className="fsr-recording__header">
        <div className="fsr-recording__header-top">
          <span className="fsr-recording__orchard">
            {orchardName}
            {cultivar ? ` · ${cultivar}` : ''}
          </span>
          <span className={['fsr-gps-pill', gpsClass, geofenceMismatch ? 'fsr-gps-pill--warn' : ''].filter(Boolean).join(' ')}>
            <span className="fsr-gps-pill__dot" aria-hidden />
            {geofenceMismatch
              ? t('fieldScout.recording.geofenceWarning')
              : accuracyDisplay}
          </span>
        </div>
      </header>

      {/* ── Progress strip ────────────────────────────────────────────────── */}
      <div className="fsr-recording__progress">
        <div className="fsr-recording__progress-row">
          <div className="fsr-recording__progress-text">
            <span>
              {t('fieldScout.recording.point')}{' '}
              <strong>{rec.currentPoint + 1}</strong>/100
              {' · '}
              {examined} {t('fieldScout.recording.examined')}
              {' · '}
              {activePct}%
            </span>
          </div>
          <button
            type="button"
            className="fsr-cancel-btn"
            onClick={handleCancel}
            data-testid="btn-cancel"
          >
            {t('fieldScout.recording.cancelSession')}
          </button>
        </div>
        <div className="fsr-recording__progress-bar">
          <div
            className="fsr-recording__progress-fill"
            style={{ width: `${progressWidth}%` }}
          />
        </div>
      </div>

      {/* ── Scrollable body ───────────────────────────────────────────────── */}
      <div className="fsr-recording__body">
        {/* ── DotGrid card ────────────────────────────────────────────────── */}
        <div className="fsr-grid-card" data-testid="dot-grid-card">
          {/* Header row: pest identity + live % */}
          <div className="fsr-grid-card__head">
            <div className="fsr-grid-card__pest-id">
              {activePest && (
                <>
                  <span className="fsr-grid-card__code">{activePest.code}</span>
                  <span className="fsr-grid-card__name">{activePest.name}</span>
                </>
              )}
            </div>
            <span
              className={['fsr-grid-card__pct', isOverThreshold ? 'fsr-grid-card__pct--over' : ''].filter(Boolean).join(' ')}
              data-testid="active-pct"
            >
              {activePct}%
            </span>
          </div>

          {/* Over/action row + Solo/All toggle */}
          <div className="fsr-grid-card__status-row">
            <span>
              {isOverThreshold && activePest ? (
                <span className="fsr-flag-pill" data-testid="over-threshold-flag">
                  {t('fieldScout.recording.over', { action: actionThreshold(activePest) })}
                </span>
              ) : activePest ? (
                <span className="fsr-action-hint">
                  {t('fieldScout.recording.actionHint', { action: actionThreshold(activePest) })}
                </span>
              ) : null}
            </span>
            <div className="fsr-mode-toggle" role="group" aria-label={t('fieldScout.recording.modeToggle')}>
              <button
                type="button"
                className={['fsr-mode-btn', rec.mode === 'solo' ? 'fsr-mode-btn--active' : ''].filter(Boolean).join(' ')}
                onClick={() => rec.setMode('solo')}
                data-testid="mode-solo"
              >
                {t('fieldScout.recording.solo')}
              </button>
              <button
                type="button"
                className={['fsr-mode-btn', rec.mode === 'all' ? 'fsr-mode-btn--active' : ''].filter(Boolean).join(' ')}
                onClick={() => rec.setMode('all')}
                data-testid="mode-all"
              >
                {t('fieldScout.recording.all')}
              </button>
            </div>
          </div>

          {/* DotGrid */}
          <DotGrid
            infested={activeInfestedSet}
            allInfested={allInfestedSet}
            currentPoint={rec.currentPoint}
            maxExamined={rec.maxExamined}
            mode={rec.mode}
            photos={photoPointSet}
            onPickPoint={rec.pickPoint}
          />

          {/* Legend */}
          <div className="fsr-grid-legend" aria-label="Legend">
            <span className="fsr-legend-item">
              <i className="fsr-legend-dot fsr-legend-dot--red" />
              {t('fieldScout.recording.legend.infested')}
            </span>
            <span className="fsr-legend-item">
              <i className="fsr-legend-dot fsr-legend-dot--green" />
              {t('fieldScout.recording.legend.clean')}
            </span>
            <span className="fsr-legend-item">
              <i className="fsr-legend-dot fsr-legend-dot--grey" />
              {t('fieldScout.recording.legend.notInspected')}
            </span>
            {rec.mode === 'all' && (
              <span className="fsr-legend-item">
                <i className="fsr-legend-dot fsr-legend-dot--faint" />
                {t('fieldScout.recording.legend.otherPest')}
              </span>
            )}
          </div>
        </div>

        {/* ── INCIDENCE section — pinned ───────────────────────────────── */}
        {incidencePests.length > 0 && (
          <Section
            title={t('fieldScout.recording.section.incidence')}
            summary={incidenceSummary}
            pinned
            open
            typeKey="inc"
          >
            <HScroll ariaLabel={t('fieldScout.recording.section.incidence')}>
              {chunk(incidencePests, COLS * 2).map((page, pi) => (
                <div
                  className="fsr-pest-page"
                  key={pi}
                  style={{ '--fsr-cols': COLS, '--fsr-rows': 2 } as React.CSSProperties}
                >
                  {page.map((p) => {
                    const c = rec.infested[p.id]?.length ?? 0;
                    const pestPct = pct(c, examined);
                    return (
                      <IncidenceButton
                        key={p.id}
                        code={p.code}
                        name={p.name}
                        pct={pestPct}
                        selected={rec.activePestId === p.id}
                        infestedHere={(rec.infested[p.id] ?? []).includes(rec.currentPoint)}
                        overThreshold={pestPct >= actionThreshold(p)}
                        onTap={() => handleToggleIncidence(p.id)}
                        onLongPress={() => rec.setActivePest(p.id)}
                      />
                    );
                  })}
                </div>
              ))}
            </HScroll>
          </Section>
        )}

        {/* ── LEVEL section — collapsible ──────────────────────────────── */}
        {levelPests.length > 0 && (
          <Section
            title={t('fieldScout.recording.section.level')}
            summary={levelSummary}
            open={levelOpen}
            onToggle={() => setLevelOpen((o) => !o)}
            typeKey="lev"
          >
            <HScroll ariaLabel={t('fieldScout.recording.section.level')}>
              {chunk(levelPests, COLS).map((page, pi) => (
                <div
                  className="fsr-pest-page"
                  key={pi}
                  style={{ '--fsr-cols': COLS, '--fsr-rows': 1 } as React.CSSProperties}
                >
                  {page.map((p) => {
                    const severity = (rec.levels[p.id] ?? 0) as 0 | 1 | 2 | 3 | 4;
                    return (
                      <LevelButton
                        key={p.id}
                        code={p.code}
                        name={p.name}
                        severity={severity}
                        active={severity > 0}
                        overThreshold={severity >= actionThreshold(p)}
                        onTap={() => handleRaiseLevel(p.id)}
                        onLongPress={() => handleLowerLevel(p.id)}
                      />
                    );
                  })}
                </div>
              ))}
            </HScroll>
          </Section>
        )}

        {/* ── COUNT section — collapsible ──────────────────────────────── */}
        {countPests.length > 0 && (
          <Section
            title={t('fieldScout.recording.section.count')}
            summary={countSummary}
            open={countOpen}
            onToggle={() => setCountOpen((o) => !o)}
            typeKey="cnt"
          >
            <HScroll ariaLabel={t('fieldScout.recording.section.count')}>
              {chunk(countPests, COLS).map((page, pi) => (
                <div
                  className="fsr-pest-page"
                  key={pi}
                  style={{ '--fsr-cols': COLS, '--fsr-rows': 1 } as React.CSSProperties}
                >
                  {page.map((p) => {
                    const value = rec.counts[p.id] ?? 0;
                    return (
                      <CountButton
                        key={p.id}
                        code={p.code}
                        name={p.name}
                        value={value}
                        active={value > 0}
                        overThreshold={value >= actionThreshold(p)}
                        onTap={() => handleIncCount(p.id)}
                        onLongPress={() => handleDecCount(p.id)}
                      />
                    );
                  })}
                </div>
              ))}
            </HScroll>
          </Section>
        )}

        {/* Bottom scroll spacer */}
        <div style={{ height: '0.5rem' }} />
      </div>

      {/* ── Undo toast ────────────────────────────────────────────────────── */}
      {undoVisible && (
        <div className="fsr-undo-toast" role="status" data-testid="undo-toast">
          <span className="fsr-undo-toast__label">{t('fieldScout.recording.undo')}</span>
          <button
            type="button"
            className="fsr-undo-toast__btn"
            onClick={handleUndo}
            data-testid="undo-btn"
          >
            {t('fieldScout.recording.undo')}
          </button>
        </div>
      )}

      {/* ── Hidden photo input ────────────────────────────────────────────── */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        aria-hidden
        tabIndex={-1}
        className="fsr-photo-input"
        onChange={handlePhotoChange}
      />

      {/* ── Fixed action bar (thumb zone) ─────────────────────────────────── */}
      <footer className="fsr-recording__actionbar">
        <button
          type="button"
          className="fsr-ab-btn fsr-ab-btn--prev"
          onClick={rec.prev}
          disabled={rec.currentPoint === 0}
          data-testid="btn-prev"
        >
          <i className="bi bi-chevron-left" aria-hidden />
          {t('fieldScout.recording.prev')}
        </button>

        <button
          type="button"
          className="fsr-ab-btn fsr-ab-btn--photo"
          onClick={handlePhotoClick}
          data-testid="btn-photo"
        >
          <i className="bi bi-camera" aria-hidden />
          {t('fieldScout.recording.photo')}
        </button>

        <button
          type="button"
          className="fsr-ab-btn fsr-ab-btn--clear"
          onClick={handleClearPoint}
          data-testid="btn-clear"
        >
          <i className="bi bi-backspace" aria-hidden />
          {t('fieldScout.recording.clear')}
        </button>

        {isLastPoint ? (
          <button
            type="button"
            className="fsr-ab-btn fsr-ab-btn--finish"
            onClick={handleFinish}
            data-testid="btn-finish"
          >
            {t('fieldScout.recording.finish')}
          </button>
        ) : (
          <button
            type="button"
            className="fsr-ab-btn fsr-ab-btn--next"
            onClick={handleNext}
            data-testid="btn-next"
          >
            {t('fieldScout.recording.saveNext')} <i className="bi bi-arrow-right" aria-hidden />
          </button>
        )}
      </footer>

      {/* ── GPS soft-block gate ───────────────────────────────────────────── */}
      {gpsGateOpen && (
        <div className="fsr-gps-gate" role="dialog" aria-modal="true" data-testid="gps-gate-modal">
          <div className="fsr-gps-gate__card">
            <h2 className="fsr-gps-gate__title">{t('fieldScout.recording.gpsGate.title')}</h2>
            <p className="fsr-gps-gate__body">
              {Number.isFinite(rec.currentAccuracy)
                ? t('fieldScout.recording.gpsGate.body', { m: Math.round(rec.currentAccuracy) })
                : t('fieldScout.recording.gpsGate.bodyNoFix')}
            </p>
            <div className="fsr-gps-gate__actions">
              <button
                type="button"
                className="fsr-gps-gate__btn fsr-gps-gate__btn--wait"
                onClick={handleGateWait}
                data-testid="gps-gate-wait"
              >
                {t('fieldScout.recording.gpsGate.wait')}
              </button>
              <button
                type="button"
                className="fsr-gps-gate__btn fsr-gps-gate__btn--override"
                onClick={handleGateOverride}
                data-testid="gps-gate-override"
              >
                {t('fieldScout.recording.gpsGate.override')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldScoutRecording;
